import React, { useState } from 'react';
import { Grid, useMediaQuery } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { AccountData, ReportsService } from '../api';
import { theme, useLanguage } from '../common/GeneralUtils';
import { DATE_FORMAT } from '../calendar/calendarWrapper/CalendarWrapper';
import { getDateFormat } from '../common/getDateAndTimeFormat';
import { useAuth } from '../session/InternalAuthProvider';
import { ExportButton } from '../common/export/ExportButton';
import { exportCsvText } from '../common/export/exportTable';
import { ExportFormat } from '../common/export/exportTypes';
import { ReportPresetControls } from './ReportPresetControls';
import { useReportPresetSelection } from './useReportPresetSelection';
import { ReportDateRangeShortcuts } from './ReportDateRangeShortcuts';

export const WorkshopsReports = () => {
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const unlimitedReportsMinDate = useFeatureFlagEnabled('unlimited-reports-min-date');
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { longDateFormat } = getDateFormat(account);
  const { t } = useTranslation();
  const language = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [fromDate, setFromDate] = useState(moment().subtract(1, 'month').startOf('month'));
  const [toDate, setToDate] = useState(moment().subtract(1, 'month').endOf('month'));
  const { columns, presets, setPresets, selectedPresetId, setSelectedPresetId, filterCsv, orderPageIds } =
    useReportPresetSelection('workshops');
  const minDate = moment().subtract(unlimitedReportsMinDate ? 36 : 6, 'months');
  const maxDate = moment().add(2, 'month');

  const handleDownload = async (format: ExportFormat) => {
    try {
      setIsLoading(true);
      const csv = await ReportsService.getWorkshopsReport(
        language,
        fromDate?.format(DATE_FORMAT),
        toDate?.format(DATE_FORMAT),
        orderPageIds,
      );
      await exportCsvText({
        csv: filterCsv(csv),
        fileName: `events-and-private-workshops-${fromDate.format(DATE_FORMAT)}-${toDate.format(DATE_FORMAT)}`,
        format,
        language,
        sheetName: t('workshopsReport.title'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid container flexDirection="column" justifyContent="center" alignItems="center">
      <Typography variant={'h4'}>{t('workshopsReport.title')}</Typography>
      <Typography variant={'caption'}>{t('workshopsReport.subTitle')}</Typography>
      <Grid container justifyContent="center" mt={4}>
        <ReportDateRangeShortcuts
          fromDate={fromDate}
          toDate={toDate}
          minDate={minDate}
          maxDate={maxDate}
          onChange={(from, to) => {
            setFromDate(from);
            setToDate(to);
          }}
        />
      </Grid>
      <Grid
        container
        flexDirection={isSmallScreen ? 'column' : 'row'}
        justifyContent="center"
        alignItems="center"
        gap={2}
        mt={2}>
        <Grid item>
          <DatePicker
            label={t('ordersReport.from')}
            value={fromDate}
            onChange={(newValue: any) => setFromDate(newValue)}
            minDate={minDate}
            format={longDateFormat}
          />
        </Grid>
        <Grid item>
          <DatePicker
            label={t('ordersReport.to')}
            value={toDate}
            onChange={(newValue: any) => setToDate(newValue)}
            minDate={fromDate}
            maxDate={maxDate}
            format={longDateFormat}
          />
        </Grid>
        <Grid item>
          <ReportPresetControls
            columns={columns}
            presets={presets}
            onPresetsChange={setPresets}
            selectedPresetId={selectedPresetId}
            onSelectedPresetIdChange={setSelectedPresetId}
          />
        </Grid>
        <Grid item>
          <ExportButton
            variant="button"
            label={t('ordersReport.download')}
            loading={isLoading}
            disabled={toDate.isBefore(fromDate)}
            onExport={handleDownload}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
